import { uiPaper, uiSurface } from "shared/ui/uiSurface"
import {
  CheckCircleOutline,
  CloudUpload,
  ErrorOutline,
  WarningAmber,
} from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { FC, useCallback, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  ClientImportRow,
  useImportClientsMutation,
} from "../../apis/appApi"

// --- Field mapping definitions ---

const IMPORT_FIELDS = [
  "skip",
  "firstName",
  "lastName",
  "email",
  "phone",
  "streetNumber",
  "streetName",
  "apartment",
  "city",
  "state",
  "postcode",
  "country",
  "notes",
  "tags",
] as const

type ImportField = (typeof IMPORT_FIELDS)[number]

/** Auto-detect column names to field mappings. */
const AUTO_DETECT_MAP: Record<string, ImportField> = {
  first: "firstName",
  firstname: "firstName",
  first_name: "firstName",
  "first name": "firstName",
  prenom: "firstName",
  prénom: "firstName",
  last: "lastName",
  lastname: "lastName",
  last_name: "lastName",
  "last name": "lastName",
  nom: "lastName",
  name: "lastName",
  email: "email",
  courriel: "email",
  "e-mail": "email",
  phone: "phone",
  telephone: "phone",
  téléphone: "phone",
  tel: "phone",
  "phone number": "phone",
  "phone_number": "phone",
  streetnumber: "streetNumber",
  street_number: "streetNumber",
  "street number": "streetNumber",
  "no": "streetNumber",
  "no.": "streetNumber",
  "numero civique": "streetNumber",
  "numéro civique": "streetNumber",
  streetname: "streetName",
  street_name: "streetName",
  "street name": "streetName",
  street: "streetName",
  rue: "streetName",
  apt: "apartment",
  apartment: "apartment",
  appartement: "apartment",
  app: "apartment",
  "app.": "apartment",
  unit: "apartment",
  city: "city",
  ville: "city",
  state: "state",
  province: "state",
  prov: "state",
  "prov.": "state",
  postcode: "postcode",
  "postal code": "postcode",
  "postal_code": "postcode",
  zip: "postcode",
  "zip code": "postcode",
  "code postal": "postcode",
  country: "country",
  pays: "country",
  note: "notes",
  notes: "notes",
  tag: "tags",
  tags: "tags",
  etiquette: "tags",
  etiquettes: "tags",
  étiquette: "tags",
  étiquettes: "tags",
}

// --- CSV parsing ---

function stripBom(text: string): string {
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text
}

function detectDelimiter(firstLine: string): string {
  const commaCount = (firstLine.match(/,/g) || []).length
  const semicolonCount = (firstLine.match(/;/g) || []).length
  return semicolonCount > commaCount ? ";" : ","
}

function parseCSVLine(line: string, delimiter: string): string[] {
  const fields: string[] = []
  let current = ""
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        current += ch
      }
    } else {
      if (ch === '"') {
        inQuotes = true
      } else if (ch === delimiter) {
        fields.push(current.trim())
        current = ""
      } else {
        current += ch
      }
    }
  }
  fields.push(current.trim())
  return fields
}

function parseCSV(text: string): { headers: string[]; rows: string[][] } {
  const cleaned = stripBom(text)
  const lines = cleaned.split(/\r?\n/).filter((l) => l.trim().length > 0)
  if (lines.length === 0) return { headers: [], rows: [] }

  const delimiter = detectDelimiter(lines[0])
  const headers = parseCSVLine(lines[0], delimiter)
  const rows = lines.slice(1).map((line) => parseCSVLine(line, delimiter))
  return { headers, rows }
}

// --- Types ---

type MappedRow = {
  data: ClientImportRow
  valid: boolean
  errors: string[]
}

type ImportResult = {
  created: number
  updated: number
  failed: number
  errors: { row: number; message: string }[]
}

type Props = {
  open: boolean
  onClose: () => void
  onSuccess: (message: string) => void
}

export const ClientImportDialog: FC<Props> = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Step state
  const [activeStep, setActiveStep] = useState(0)

  // Step 1: Upload
  const [fileName, setFileName] = useState("")
  const [csvHeaders, setCsvHeaders] = useState<string[]>([])
  const [csvRows, setCsvRows] = useState<string[][]>([])

  // Step 2: Mapping
  const [columnMapping, setColumnMapping] = useState<ImportField[]>([])

  // Step 3: Preview (computed)
  // Step 4: Results
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [showErrors, setShowErrors] = useState(false)

  const [importClients, { isLoading: isImporting }] =
    useImportClientsMutation()

  const steps = [
    t("clients.import.step.upload"),
    t("clients.import.step.mapping"),
    t("clients.import.step.preview"),
    t("clients.import.step.results"),
  ]

  // --- Step 1: File handling ---

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = (evt) => {
        const text = evt.target?.result as string
        const { headers, rows } = parseCSV(text)
        setCsvHeaders(headers)
        setCsvRows(rows)
        setFileName(file.name)

        // Auto-detect column mappings
        const autoMapping = headers.map((h) => {
          const normalized = h.toLowerCase().trim()
          return AUTO_DETECT_MAP[normalized] ?? ("skip" as ImportField)
        })
        setColumnMapping(autoMapping)
      }
      reader.readAsText(file, "utf-8")

      // Reset file input so same file can be re-selected
      e.target.value = ""
    },
    [],
  )

  // --- Step 2: Mapping ---

  const handleMappingChange = useCallback(
    (colIndex: number, value: ImportField) => {
      setColumnMapping((prev) => {
        const next = [...prev]
        next[colIndex] = value
        return next
      })
    },
    [],
  )

  const isMappingValid = useMemo(() => {
    return (
      columnMapping.includes("firstName") &&
      columnMapping.includes("lastName")
    )
  }, [columnMapping])

  // --- Step 3: Preview & Validate ---

  const mappedRows = useMemo((): MappedRow[] => {
    if (!isMappingValid) return []

    return csvRows.map((row) => {
      const data: Record<string, any> = {}
      const errors: string[] = []

      columnMapping.forEach((field, colIdx) => {
        if (field === "skip") return
        const value = row[colIdx] ?? ""
        if (field === "tags") {
          // Split tags by semicolon
          data.tags = value
            .split(";")
            .map((t) => t.trim())
            .filter(Boolean)
        } else {
          data[field] = value || null
        }
      })

      // Validate required fields
      const missingFields: string[] = []
      if (!data.firstName || !(data.firstName as string).trim()) {
        missingFields.push(t("clients.import.mapping.firstName"))
      }
      if (!data.lastName || !(data.lastName as string).trim()) {
        missingFields.push(t("clients.import.mapping.lastName"))
      }
      if (missingFields.length > 0) {
        errors.push(
          t("clients.import.preview.errorMissing", {
            fields: missingFields.join(", "),
          }),
        )
      }

      return {
        data: data as ClientImportRow,
        valid: errors.length === 0,
        errors,
      }
    })
  }, [csvRows, columnMapping, isMappingValid, t])

  const validRows = useMemo(
    () => mappedRows.filter((r) => r.valid),
    [mappedRows],
  )
  const invalidRows = useMemo(
    () => mappedRows.filter((r) => !r.valid),
    [mappedRows],
  )

  // --- Step 4: Import ---

  const handleImport = useCallback(async () => {
    try {
      const result = await importClients({
        rows: validRows.map((r) => r.data),
      }).unwrap()
      setImportResult(result)
      setActiveStep(3)
      if (result.failed === 0) {
        onSuccess(t("clients.import.results.success"))
      }
    } catch {
      setImportResult({
        created: 0,
        updated: 0,
        failed: validRows.length,
        errors: [{ row: 0, message: "Import request failed" }],
      })
      setActiveStep(3)
    }
  }, [importClients, validRows, onSuccess, t])

  // --- Navigation ---

  const handleNext = useCallback(() => {
    if (activeStep === 2) {
      handleImport()
    } else {
      setActiveStep((s) => s + 1)
    }
  }, [activeStep, handleImport])

  const handleBack = useCallback(() => {
    setActiveStep((s) => s - 1)
  }, [])

  const handleClose = useCallback(() => {
    // Reset all state
    setActiveStep(0)
    setFileName("")
    setCsvHeaders([])
    setCsvRows([])
    setColumnMapping([])
    setImportResult(null)
    setShowErrors(false)
    onClose()
  }, [onClose])

  // Determine if Next is disabled
  const isNextDisabled = (() => {
    if (activeStep === 0) return csvRows.length === 0
    if (activeStep === 1) return !isMappingValid
    if (activeStep === 2) return validRows.length === 0 || isImporting
    return false
  })()

  // Preview rows for mapping step (first 3)
  const previewRows = csvRows.slice(0, 3)

  return (
    <Dialog
      slotProps={{ paper: uiPaper("UI-DLV2-096") }}
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      data-testid="client-import-dialog"
    >
      <DialogTitle>{t("clients.import.title")}</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 1: Upload */}
        {activeStep === 0 && (
          <Box {...uiSurface("UI-DLV2-097")} sx={{ textAlign: "center", py: 4 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
            <Box
              sx={{
                border: "2px dashed",
                borderColor: "grey.300",
                borderRadius: 2,
                p: 4,
                cursor: "pointer",
                "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <CloudUpload sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                {t("clients.import.upload.dropzone")}
              </Typography>
              <Button variant="outlined" size="small" sx={{ mt: 2 }}>
                {t("clients.import.upload.browse")}
              </Button>
            </Box>
            <Box sx={{ mt: 2 }}>
              {fileName ? (
                <Typography variant="body2" color="text.primary">
                  {t("clients.import.upload.fileSelected", {
                    name: fileName,
                    count: csvRows.length,
                  })}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("clients.import.upload.noFile")}
                </Typography>
              )}
            </Box>
            {csvRows.length > 5000 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Maximum 5000 rows allowed. Only the first 5000 will be
                processed.
              </Alert>
            )}
          </Box>
        )}

        {/* Step 2: Column Mapping */}
        {activeStep === 1 && (
          <Box {...uiSurface("UI-DLV2-098")}>
            {!isMappingValid && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t("clients.import.mapping.required")}
              </Alert>
            )}
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>
                      {t("clients.import.mapping.column")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>
                      {t("clients.import.mapping.mapTo")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("clients.import.mapping.preview")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {csvHeaders.map((header, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {header}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <InputLabel>{t("clients.import.mapping.mapTo")}</InputLabel>
                          <Select
                            value={columnMapping[idx] ?? "skip"}
                            onChange={(e) =>
                              handleMappingChange(
                                idx,
                                e.target.value as ImportField,
                              )
                            }
                            label={t("clients.import.mapping.mapTo")}
                          >
                            {IMPORT_FIELDS.map((field) => (
                              <MenuItem key={field} value={field}>
                                {field === "skip"
                                  ? t("clients.import.mapping.skip")
                                  : t(`clients.import.mapping.${field}`)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          {previewRows.map((row, rowIdx) => (
                            <Typography
                              key={rowIdx}
                              variant="caption"
                              sx={{
                                bgcolor: "grey.100",
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 0.5,
                                maxWidth: 120,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "inline-block",
                              }}
                            >
                              {row[idx] || "-"}
                            </Typography>
                          ))}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Step 3: Preview & Validate */}
        {activeStep === 2 && (
          <Box {...uiSurface("UI-DLV2-099")}>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Chip
                label={t("clients.import.preview.total", {
                  count: mappedRows.length,
                })}
                variant="outlined"
              />
              <Chip
                icon={<CheckCircleOutline />}
                label={t("clients.import.preview.valid", {
                  count: validRows.length,
                })}
                color="success"
                variant="outlined"
              />
              {invalidRows.length > 0 && (
                <Chip
                  icon={<ErrorOutline />}
                  label={t("clients.import.preview.invalid", {
                    count: invalidRows.length,
                  })}
                  color="error"
                  variant="outlined"
                />
              )}
            </Stack>

            {isImporting && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {t("clients.import.importing")}
                </Typography>
              </Box>
            )}

            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("clients.import.mapping.firstName")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("clients.import.mapping.lastName")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("clients.import.mapping.email")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("clients.import.mapping.phone")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("clients.import.mapping.city")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappedRows.map((row, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        bgcolor: row.valid ? undefined : "error.50",
                        "&:hover": { bgcolor: row.valid ? undefined : "error.100" },
                      }}
                    >
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{row.data.firstName || "-"}</TableCell>
                      <TableCell>{row.data.lastName || "-"}</TableCell>
                      <TableCell>{row.data.email || "-"}</TableCell>
                      <TableCell>{row.data.phone || "-"}</TableCell>
                      <TableCell>{row.data.city || "-"}</TableCell>
                      <TableCell>
                        {row.valid ? (
                          <CheckCircleOutline
                            fontSize="small"
                            color="success"
                          />
                        ) : (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <WarningAmber
                              fontSize="small"
                              color="error"
                            />
                            <Typography variant="caption" color="error">
                              {row.errors[0]}
                            </Typography>
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Step 4: Results */}
        {activeStep === 3 && importResult && (
          <Box {...uiSurface("UI-DLV2-100")} sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t("clients.import.results.title")}
            </Typography>
            <Stack spacing={1.5} sx={{ mb: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleOutline color="success" />
                <Typography>
                  {t("clients.import.results.created", {
                    count: importResult.created,
                  })}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleOutline color="info" />
                <Typography>
                  {t("clients.import.results.updated", {
                    count: importResult.updated,
                  })}
                </Typography>
              </Stack>
              {importResult.failed > 0 && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <ErrorOutline color="error" />
                  <Typography color="error">
                    {t("clients.import.results.failed", {
                      count: importResult.failed,
                    })}
                  </Typography>
                </Stack>
              )}
            </Stack>

            {importResult.errors.length > 0 && (
              <>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setShowErrors((s) => !s)}
                >
                  {t("clients.import.results.errors")} (
                  {importResult.errors.length})
                </Button>
                <Collapse in={showErrors}>
                  <Box
                    sx={{
                      mt: 1,
                      maxHeight: 200,
                      overflow: "auto",
                      bgcolor: "grey.50",
                      borderRadius: 1,
                      p: 1.5,
                    }}
                  >
                    {importResult.errors.map((err, idx) => (
                      <Typography
                        key={idx}
                        variant="caption"
                        display="block"
                        color="error"
                      >
                        {t("clients.import.results.errorRow", {
                          row: err.row,
                          message: err.message,
                        })}
                      </Typography>
                    ))}
                  </Box>
                </Collapse>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {activeStep > 0 && activeStep < 3 && (
          <Button onClick={handleBack} disabled={isImporting}>
            {t("clients.import.back")}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {activeStep < 3 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={isNextDisabled}
          >
            {activeStep === 2
              ? isImporting
                ? t("clients.import.importing")
                : t("clients.import.preview.importBtn", {
                    count: validRows.length,
                  })
              : t("clients.import.next")}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleClose}>
            {t("clients.import.results.done")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
